import { useFeatureFlag } from '@user-portal/experimentation';

import { parseBooleanParam } from 'helpers/url';

import { getAbAutoTestsQuery } from './helpers';

type FeatureFlagType = 'boolean' | 'string' | 'number' | 'integer' | 'json';

interface useFeatureFlagWithAQAOptions<T> {
    flagKey: string;
    defaultValue: T;
    type?: FeatureFlagType;
}

export const useFeatureFlagWithAQA = <T extends boolean | string | number | object = boolean>(
    options: useFeatureFlagWithAQAOptions<T>
): { value: T; isReady: boolean } => {
    const { flagKey, defaultValue, type = 'boolean' } = options;

    const result = useFeatureFlag<T>(options);

    const { aqa, experiment, variantName, experimentOptions, isEnabled } = getAbAutoTestsQuery();

    let aqaValue: T | undefined;

    if (aqa && flagKey === experiment) {
        switch (type) {
            case 'boolean':
                aqaValue = (parseBooleanParam(isEnabled) ?? defaultValue) as T;
                break;

            case 'string':
                aqaValue = (variantName || defaultValue) as T;
                break;

            case 'json':
                try {
                    aqaValue = (experimentOptions ? JSON.parse(experimentOptions) : defaultValue) as T;
                } catch {
                    aqaValue = defaultValue;
                }
                break;

            case 'number': {
                const n = Number(variantName);
                aqaValue = (Number.isFinite(n) ? n : defaultValue) as T;
                break;
            }

            case 'integer': {
                const n = parseInt(variantName || '', 10);
                aqaValue = (Number.isFinite(n) ? n : defaultValue) as T;
                break;
            }
        }
    }

    return aqaValue !== undefined ? { value: aqaValue, isReady: true } : result;
};
